import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useV2Auth } from '@/context/V2AuthContext';
import { PrimaryAction } from '@/components/mask/MaskSystem';
import { CarmaExperience } from '@/components/CarmaExperience';
import { dateLocale } from '@/lib/dateLocale';
import '@/components/mask/mask.css';

/* Phase 3 — FÜR DICH: persönlicher Carma-Raum. Moment für Moment statt Posteingang. */

const fragment = (text, len = 80) => (text.length > len ? `${text.slice(0, len).trimEnd()} …` : text);

export default function FuerDichV2() {
  const { user, API_V2 } = useV2Auth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState(null);
  const [experience, setExperience] = useState(null); // {carma, isDiscovery}

  const dateShort = (iso) => new Date(iso).toLocaleDateString(dateLocale(i18n.language), { day: 'numeric', month: 'long', year: 'numeric' });

  useEffect(() => {
    document.body.classList.add('cc-no-overlays');
    return () => document.body.classList.remove('cc-no-overlays');
  }, []);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API_V2}/carma/room`, { withCredentials: true })
      .then((res) => setItems(res.data))
      .catch((err) => {
        if (err.response?.status === 401) navigate('/auth', { replace: true });
        else setItems([]);
      });
  }, [user, API_V2, navigate]);

  const patch = (id, changes) => setItems((prev) => prev.map((m) => (m.id === id ? { ...m, ...changes } : m)));

  const openCarma = (id) => {
    axios.post(`${API_V2}/carma/${id}/open`, {}, { withCredentials: true }).catch(() => {});
    patch(id, { readAt: new Date().toISOString() });
    window.dispatchEvent(new Event('cc-unread-refresh'));
  };
  const keepCarma = async (id) => {
    const res = await axios.post(`${API_V2}/carma/${id}/keep`, {}, { withCredentials: true });
    patch(id, { keptAt: res.data.keptAt });
    setExperience((e) => (e && e.carma.id === id ? { ...e, carma: { ...e.carma, keptAt: res.data.keptAt } } : e));
  };
  const unkeepCarma = async (id) => {
    await axios.post(`${API_V2}/carma/${id}/unkeep`, {}, { withCredentials: true });
    patch(id, { keptAt: null });
    setExperience((e) => (e && e.carma.id === id ? { ...e, carma: { ...e.carma, keptAt: null } } : e));
  };
  const saveNote = async (id, text) => {
    const res = await axios.put(`${API_V2}/carma/${id}/note`, { text }, { withCredentials: true });
    patch(id, { note: res.data.note });
    setExperience((e) => (e && e.carma.id === id ? { ...e, carma: { ...e.carma, note: res.data.note } } : e));
  };

  if (!user || items === null) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <p className="text-sm text-[var(--muted)]">{t('v2.common.loading')}</p>
      </div>
    );
  }

  const discover = [...items].filter((m) => !m.readAt).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const arrived = items.filter((m) => m.readAt && !m.keptAt);
  const kept = items.filter((m) => m.keptAt);
  const current = discover[0];
  const waitingOthers = discover.slice(1);
  const uniqueSenders = new Set(discover.map((m) => m.senderId)).size;

  const subline = discover.length === 0
    ? null
    : uniqueSenders === 1
      ? t('v2.fuerdich.subOne')
      : t('v2.fuerdich.subMany', { num: t(`v2.numWords.${uniqueSenders}`, { defaultValue: String(uniqueSenders) }) });

  // Empty State — kein leerer Posteingang, sondern ein Moment
  if (items.length === 0) {
    return (
      <div className="relative min-h-[calc(100vh-4rem)] overflow-x-hidden flex flex-col" data-testid="fuerdich-empty">
        <div className="mask-bg-word" aria-hidden="true">
          <span style={{ transform: 'translateX(-10%)' }}>{t('v2.bgWords.fuerDich')}</span>
        </div>
        <div className="relative z-10 flex-1 flex flex-col justify-center items-center text-center w-full max-w-xl mx-auto px-6 py-24">
          <h1 className="mask-enter mask-headline text-3xl sm:text-4xl lg:text-5xl text-[var(--text)] mb-8">
            {t('v2.fuerdich.emptyHeadline')}
          </h1>
          <p className="mask-enter mask-enter-delay-1 text-base text-[var(--muted)] leading-relaxed mb-10 max-w-md">
            {t('v2.fuerdich.emptyText')}
          </p>
          <div className="mask-enter mask-enter-delay-2">
            <PrimaryAction onClick={() => navigate('/give')} data-testid="empty-give-btn">{t('v2.common.giveCarma')}</PrimaryAction>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-[calc(100vh-4rem)] overflow-x-hidden" data-testid="fuerdich-room">
      <div className="mask-bg-word mask-bg-top" aria-hidden="true">
        <span style={{ transform: 'translateX(-10%)' }}>{t('v2.bgWords.fuerDich')}</span>
      </div>

      <div className="relative z-10 w-full max-w-2xl mx-auto px-6 sm:px-8 pt-20 pb-28">
        {/* Einstieg */}
        <header className="mb-20">
          <p className="mask-enter text-xs uppercase tracking-[0.2em] text-[var(--muted)] mb-5">{t('v2.fuerdich.label')}</p>
          <h1 className="mask-enter mask-enter-delay-1 mask-headline text-3xl sm:text-4xl text-[var(--text)]" data-testid="fuerdich-greeting">
            {discover.length > 0 ? t('v2.fuerdich.headlineDiscover') : t('v2.fuerdich.headlineWords')}
          </h1>
          {subline && (
            <p className="mask-enter mask-enter-delay-2 text-base text-[var(--muted)] mt-5" data-testid="fuerdich-subline">{subline}</p>
          )}
        </header>

        {/* ENTDECKEN — ein Mensch, ein Moment */}
        {current && (
          <section className="mb-24" data-testid="discover-focus">
            <p className="mask-enter mask-enter-delay-2 text-xs uppercase tracking-[0.2em] text-[var(--muted)] mb-6">{t('v2.fuerdich.discover')}</p>
            <p className="mask-enter mask-enter-delay-2 mask-headline text-2xl sm:text-3xl text-[var(--text)] mb-2">
              {t('v2.fuerdich.leftYou', { name: current.senderName })}
            </p>
            <p className="mask-enter mask-enter-delay-3 text-sm text-[var(--muted)] mb-8">{dateShort(current.createdAt)}</p>
            <div className="mask-enter mask-enter-delay-3">
              <PrimaryAction onClick={() => setExperience({ carma: current, isDiscovery: true })} data-testid="discover-experience-btn">
                {t('v2.fuerdich.experience')}
              </PrimaryAction>
            </div>

            {waitingOthers.length > 0 && (
              <div className="mt-12 space-y-1.5" data-testid="discover-more-hint">
                {waitingOthers.slice(0, 3).map((m) => (
                  <p key={m.id} className="mask-headline text-lg text-[var(--text)] opacity-25 select-none">
                    {t('v2.fuerdich.alsoWaiting', { name: m.senderName.split(' ')[0] })}
                  </p>
                ))}
                {waitingOthers.length > 3 && (
                  <p className="mask-headline text-lg text-[var(--text)] opacity-15 select-none">…</p>
                )}
              </div>
            )}
          </section>
        )}

        {/* ANGEKOMMEN — ruhige räumliche Sammlung */}
        {arrived.length > 0 && (
          <section className="mb-24" data-testid="arrived-section">
            <p className="text-xs uppercase tracking-[0.2em] text-[var(--muted)] mb-8">{t('v2.fuerdich.arrived')}</p>
            <div className="space-y-9">
              {arrived.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setExperience({ carma: m, isDiscovery: false })}
                  data-testid={`arrived-item-${m.id}`}
                  className="mask-room-item block w-full text-left"
                >
                  <span className="block text-xs uppercase tracking-[0.15em] text-[var(--muted)] mb-1.5">{m.senderName}</span>
                  <span className="block mask-headline text-lg sm:text-xl text-[var(--text)]/60">„{fragment(m.message)}“</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* BEWAHRT — noch ruhiger */}
        {kept.length > 0 && (
          <section className="mb-24 relative" data-testid="kept-section">
            <p className="text-xs uppercase tracking-[0.2em] text-[var(--muted)] mb-8">{t('v2.fuerdich.kept')}</p>
            <div className="space-y-12">
              {kept.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setExperience({ carma: m, isDiscovery: false })}
                  data-testid={`kept-item-${m.id}`}
                  className="mask-room-item block w-full text-left"
                >
                  <span className="block mask-headline text-xl sm:text-2xl text-[var(--text)]/85 leading-relaxed">„{fragment(m.message, 120)}“</span>
                  <span className="block text-xs uppercase tracking-[0.15em] text-[var(--muted)] mt-2.5">{m.senderName} · {dateShort(m.createdAt)}</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Sekundäre Übersicht */}
        <footer className="pt-6">
          <button
            type="button"
            onClick={() => navigate('/inbox-classic')}
            data-testid="fuerdich-all-link"
            className="text-sm text-[var(--muted)] hover:text-[var(--text)] underline underline-offset-4 decoration-[var(--muted)]/40 transition-colors"
          >
            {t('v2.fuerdich.seeAll')}
          </button>
        </footer>
      </div>

      {experience && (
        <CarmaExperience
          carma={experience.carma}
          isDiscovery={experience.isDiscovery}
          onOpen={openCarma}
          onKeep={keepCarma}
          onUnkeep={unkeepCarma}
          onSaveNote={saveNote}
          onClose={() => setExperience(null)}
        />
      )}
    </div>
  );
}
